<template>
  <AttrGroup title="排版" :show-add="false">
    <!-- 字体 字重 字号  -->
    <div class="">
      <FontFamily :shape="shape" upload-font />
    </div>

    <!-- 字号 -->
    <div class="mt-2.5">
      <FontSizeGap :shape="shape" />
    </div>

    <!-- 字体颜色 -->
    <div class="mt-2.5">
      <AttrColor
        class="mt-2.5"
        :value="displayColor"
        alpha-hidden
        @change="onColorChange"
      >
        <template #extra>
          <span v-if="false" class="text-white/30">(Mixed)</span>
        </template>
      </AttrColor>
    </div>

    <!-- 对齐方式和宽高 -->
    <div class="mt-2.5 grid grid-cols-2 gap-2.5">
      <AttrAlign
        :value="shape?.attrs?.align"
        :disabled="
          recolorMode ||
          (!!shape.attrs.sizeContent && shape.attrs.style == 'normal') ||
          !!attrDisabled
        "
        :min="-600"
        @change="(v:any)=>shape.setAttrs({'align': v})"
      />
    </div>
  </AttrGroup>

  <!-- 类型 -->
  <div class="mt-2.5">
    <AttrSelect
      label="类型"
      :value="shape.attrs.textType"
      :disabled="recolorMode || !!attrDisabled || isAttrDisabled('textType')"
      :options="typeOptions"
      :tooltip="
        props.shape.attrs.textType === 'dateWeek'
          ? '文本框内容填写从星期一到星期日的7条内容'
          : ''
      "
      @change="(val:any) => onTextTypeChange(val)"
    />
  </div>
  <div class="mt-2.5">
    <AttrInputNumber
      class=""
      :value="shape.attrs['literalTextLength'] ?? 0"
      label="最大字数"
      :min="0"
      :max="maxCharLength"
      :precision="0"
      :tooltip="maxCharTip"
      :disabled="recolorMode || !!attrDisabled || isAttrDisabled('literalTextLength')"
      @change="(val:any)=> shape.setAttrs({ 'literalTextLength':val})"
    />
  </div>

  <!-- 是否换行 -->
  <div class="mt-2.5">
    <TextWrapMode :shape="shape" :disabled="recolorMode || isAttrDisabled('longMode')" />
  </div>

  <!-- 行间距 -->
  <div class="mt-2.5">
    <AttrInputNumber
      class=""
      :value="shape.attrs['lineSpace'] ?? 0"
      label="行间距"
      :min="-900"
      :step="1"
      :width="80"
      :precision="0"
      :disabled="recolorMode || !!attrDisabled || shape.attrs.longMode !== 'wrap'"
      addon-after="px"
      @change="(val:any)=> shape.setAttrs({ 'lineSpace':val})"
    />
  </div>

  <AttrGroup
    title="文字列表"
    :show-add="
      !recolorMode &&
      showAdd && (!!attrDisabled || isAttrDisabled('texts') ? false : true)
    "
    :card="false"
    :visible="true"
    @add="() => textOperate('add', Infinity)"
  >
    <AttrInput
      v-for="(item, index) in textsArr"
      :key="index + props.shape.id"
      :value="item"
      :shape="shape"
      :show-delete="
        !recolorMode &&
        showDelete && (!!attrDisabled || isAttrDisabled('texts') ? false : true)
      "
      :show-select="!recolorMode"
      :selected="!recolorMode && shape.attrs.currentTextIndex === index"
      :show-label="true"
      :label="genLabel(index)"
      :disabled="recolorMode || !!attrDisabled || isAttrDisabled('texts')"
      :min="1"
      :max="shape.attrs['literalTextLength']"
      required
      @delete="() => !recolorMode && !isAttrDisabled('texts') && textOperate('del', index)"
      @select="() => !recolorMode && textOperate('select', index)"
      @change="(val: string)=> onTextChange(val, index)"
    />
  </AttrGroup>
</template>

<script lang="ts" setup>
import { useEmitterListener, useWatchfaceText } from '@/hooks';
import { Shape } from '@/services/shape';
import { TextWatchfaceAttribute } from '@/services/shape/attributes/text-watchface';
import { useEditorStore } from '@/store/use-editor-store';
import { PropType, computed } from 'vue';
import { useLayerStore } from '@/store/use-layer-store';
import { EventType } from '@/utils/emitter.utils';
import { Attributes } from '@/services/shape/attributes';
import { cloneDeep } from 'lodash-es';
import { Modal } from 'ant-design-vue';
import { getCharLength } from '@/utils/richtext.util';

const props = defineProps({
  shape: {
    type: Object as PropType<Shape>,
    required: true
  },
  recolorMode: {
    type: Boolean,
    default: false
  }
});

const { getDefaultTexts } = useWatchfaceText();
const editorStore = useEditorStore();
const layerStore = useLayerStore();
const attrDisabled = computed(() => editorStore.attrDisabled);

// 当前配色ID
const currentColorId = computed(() => editorStore.themeColorId ?? '');

// 重着色模式下，颜色从 recolorColors 中按配色ID读取；否则直接用 shape.attrs.color
const displayColor = computed(() => {
  if (props.recolorMode && currentColorId.value) {
    return (
      props.shape.attrs.recolorColors?.[currentColorId.value] ??
      props.shape.attrs.color
    );
  }
  return props.shape.attrs.color;
});

// 颜色修改：重着色模式下写入 recolorColors[colorId]，否则直接修改 color
const onColorChange = (v: string) => {
  if (props.recolorMode && currentColorId.value) {
    const recolorColors = { ...(props.shape.attrs.recolorColors ?? {}) };
    recolorColors[currentColorId.value] = v;
    props.shape.setAttrs({ recolorColors });
  } else {
    props.shape.setAttrs({ color: v });
  }
};

const showAdd = computed(() => {
  const { attrs } = props.shape;
  if (!attrs.texts) return true;
  return (
    (attrs.textType === 'slogan' && attrs.texts?.length < 10) ||
    (attrs.textType === 'dateWeek' && attrs.texts?.length < 7)
  );
});

const showDelete = computed(() => {
  const { textType, texts } = props.shape.attrs;
  if (textType === 'dateWeek') return false;
  if (!texts || texts?.length <= 1) return false;
  return true;
});

const typeOptions = computed<
  {
    label: string;
    value: TextWatchfaceAttribute['textType'];
  }[]
>(() => [
  {
    label: '标语文字',
    value: 'slogan'
  },
  {
    label: '星期文字',
    value: 'dateWeek'
  }
]);

const onTextTypeChange = (type: TextWatchfaceAttribute['textType']) => {
  const _last = [...editorStore.textWatchfaceLastInput];
  editorStore.textWatchfaceLastInput = [...(props.shape.attrs.texts ?? [])];

  const texts = _last.length ? [..._last] : getDefaultTexts(type);
  // 表盘类型变化后，需要重置文字列表
  props.shape.setAttrs({
    textType: type,
    texts: texts,
    currentTextIndex: 0,
    literalTextLength: Math.min(12, props.shape.attrs.literalTextLength || 0)
  });
};

const textsArr = computed(() => {
  console.log(props.shape.attrs.texts);

  return [...(props.shape.attrs.texts ?? [])];
});

const maxCharLength = computed(() => {
  const { texts = [] } = props.shape.attrs;
  return 255 - texts.length;
});

const maxCharTip = computed(() => {
  const { attrs } = props.shape;
  return attrs.textType === 'slogan'
    ? '所有标语内容字数合计不得超过(255-N)英文字符，1个中文字符=3个英文字符'
    : '所有星期内容字数合计不得超过248英文字符，1个中文字符=3个英文字符';
});

const genLabel = (index: number) => {
  const week = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
  const slogan = [
    '标语一',
    '标语二',
    '标语三',
    '标语四',
    '标语五',
    '标语六',
    '标语七',
    '标语八',
    '标语九',
    '标语十'
  ];
  const { textType } = props.shape.attrs;

  if (textType === 'dateWeek') return week[index] + ' :';
  return `${slogan[index]} :`;
};
const textOperate = (type: 'add' | 'del' | 'select', index: number) => {
  switch (type) {
    case 'add': {
      const { texts = [] } = props.shape.attrs;
      texts.push('');
      props.shape.setAttrs({ texts });
      break;
    }
    case 'del': {
      let { texts = [] } = props.shape.attrs;
      texts.splice(index, 1);
      props.shape.setAttrs({ texts });
      break;
    }
    case 'select': {
      props.shape.setAttrs({
        currentTextIndex: index
      });
      break;
    }
  }
};

const onTextChange = (val: string, index: number) => {
  const { texts = [], literalTextLength = 0, textType } = props.shape.attrs;
  const curTexts = [...texts];
  curTexts[index] = val;
  if (
    getCharLength(curTexts?.join('') || '') > maxCharLength.value &&
    val.length > texts[index].length
  ) {
    Modal.warning({
      title: '提示',
      content:
        textType === 'slogan'
          ? '标语文字总长度超过(255-N)英文字符限制'
          : '星期文字总长度超过248英文字符限制'
    });
    return;
  }
  if (val.includes('#')) {
    Modal.warning({
      title: '提示',
      content: '文字中不能包含 #'
    });
    return;
  }
  const _texts = [...texts];
  _texts[index] = val;
  props.shape.setAttrs({
    texts: _texts
  });
};

// 禁用属性
const isFirstTheme = computed(() => {
  return layerStore.themes[0].id === editorStore.themeId;
});

// 当前选中的 shape
const firstThemeShape = computed(() => {
  const shapeId = layerStore.themes[0].children.find(
    (child) => child.type === 'TextWatchface'
  )?.id;
  return Shape.getShape(shapeId ?? '');
});

const disabledAttrKeys = ['literalTextLength', 'textType', 'texts', 'longMode'];
const isAttrDisabled = (key: keyof TextWatchfaceAttribute) => {
  if (isFirstTheme.value) return false;
  if (!disabledAttrKeys.includes(key)) return true;

  const attrs0 = firstThemeShape.value?.attrs;
  const attrs1 = props.shape.attrs;

  if (key == 'texts') {
    return attrs0?.texts?.join(',') === attrs1?.texts?.join(',');
  }
  return attrs0?.[key] === attrs1[key];
};

useEmitterListener(
  [EventType['shape:set-attr']],
  (data: { shapeId?: string; attrs?: (keyof Attributes)[] } = {}) => {
    const shape = Shape.getShape(data.shapeId ?? '');
    // 只有当前选中的 shape 是第一个theme下才需要处理
    if (!shape || shape.id != firstThemeShape.value?.id) return;
    // 需要同步其他shape
    const shapes = layerStore.themes
      .map((theme) => {
        const shapeId = theme.children.find(
          (child) => child.type === 'TextWatchface'
        )?.id;
        return Shape.getShape(shapeId ?? '');
      })
      .filter((item) => !!item && item.id != shape.id);
    // 查找需要同步的元素
    const attrs2 =
      data.attrs?.filter((key) => disabledAttrKeys.includes(key)) ?? [];
    if (!attrs2.length) return;
    shapes.forEach((shape2) => {
      attrs2.forEach((key) => {
        const val1 = shape.attrs[key];
        const val2 = shape2?.attrs[key];
        const same =
          key == 'texts'
            ? shape.attrs?.['texts']?.join(',') ==
              shape2?.attrs?.['texts']?.join(',')
            : val1 === val2;
        if (!same) {
          shape2?.setAttrs({
            [key]: cloneDeep(val1)
          });
        }
      });
    });
  }
);
</script>
